import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, isApiError } from '../api/http';
import { useI18n } from '../i18n';
import { useFieldError } from './fields';

// Cloudflare Turnstile, the check that a form is sent by a person (TF-112, docs/adr/0123-bot-controls.md):
// the sign-up form and registering a company. Cloudflare's script is loaded only where the installation
// has a site key, and only on those forms. Without a site key (development, tests) the form sends the
// test token, which only the services' recorded mode accepts. Most people never see a puzzle.

/** The token Cloudflare's test keys produce (the services' TURNSTILE_TEST_TOKEN). */
export const TEST_TOKEN = 'XXXX.DUMMY.TOKEN.XXXX';
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface PublicSettings {
  signupOpen: boolean;
  turnstileSiteKey: string | null;
}

/** Whether people may sign up on this installation, and the bot check's site key: asked without signing in. */
export function usePublicSettings() {
  return useQuery({
    queryKey: ['public-settings'],
    queryFn: () => api<PublicSettings>('/api/tenant/v1/public/signup-settings', { anonymous: true }),
    staleTime: 10 * 60_000,
  });
}

interface Turnstile {
  render(element: HTMLElement, options: Record<string, unknown>): string;
  reset(id: string): void;
  remove(id: string): void;
}

let script: Promise<Turnstile> | null = null;
function loadTurnstile(): Promise<Turnstile> {
  script ??= new Promise<Turnstile>((resolve, reject) => {
    const element = document.createElement('script');
    element.src = SCRIPT;
    element.async = true;
    element.onload = () => {
      const turnstile = (window as unknown as { turnstile?: Turnstile }).turnstile;
      if (turnstile) resolve(turnstile);
      else reject(new Error('Turnstile is missing'));
    };
    element.onerror = () => reject(new Error('Turnstile did not load'));
    document.head.appendChild(element);
  }).catch((error: unknown) => {
    script = null;
    throw error;
  });
  return script;
}

export interface BotCheckState {
  /** What to send with the form: null until the check has passed. */
  token: string | null;
  /**
   * The widget could not load (blocked, offline, a host the widget does not allow): the form may still be
   * sent, without a token, and the service decides - it refuses where the check is required.
   */
  failed: boolean;
  /** Where Cloudflare draws its widget; nothing without a site key. */
  widget: ReactNode;
  /** After the form was refused: a token is good for one answer only. */
  reset: () => void;
}

/**
 * The check for one form. `action` names the form to Cloudflare (signup, company, a website form); the service accepts a
 * token only for the form it was made for.
 */
export function useBotCheck(action: 'signup' | 'company' | 'form' | 'report'): BotCheckState {
  const { t, language } = useI18n();
  const settings = usePublicSettings();
  const siteKey = settings.data?.turnstileSiteKey ?? null;
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const element = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const turnstile = useRef<Turnstile | null>(null);

  useEffect(() => {
    if (!siteKey || !element.current) return;
    let cancelled = false;
    loadTurnstile().then(
      (loaded) => {
        if (cancelled || !element.current) return;
        turnstile.current = loaded;
        widgetId.current = loaded.render(element.current, {
          sitekey: siteKey,
          action,
          language,
          size: 'flexible',
          callback: (value: string) => setToken(value),
          'expired-callback': () => setToken(null),
          'error-callback': () => {
            setToken(null);
            setFailed(true);
          },
        });
      },
      () => setFailed(true),
    );
    return () => {
      cancelled = true;
      if (widgetId.current) turnstile.current?.remove(widgetId.current);
      widgetId.current = null;
      setToken(null);
    };
  }, [siteKey, language, action]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetId.current) turnstile.current?.reset(widgetId.current);
  }, []);

  if (settings.isPending) return { token: null, failed: false, widget: null, reset };
  // Without a site key there is nothing to draw, but what the service said about the check still shows.
  if (!siteKey) return { token: TEST_TOKEN, failed: false, widget: <BotCheckRefusal action={action} />, reset };
  return {
    token,
    failed,
    reset,
    widget: (
      <div className="bot-check">
        <div ref={element} />
        {failed && (
          <p className="field-error" role="alert">
            {t('bots.notLoaded')}
          </p>
        )}
        <BotCheckRefusal action={action} />
      </div>
    ),
  };
}

/**
 * Why the form was refused at the check (TF-216), beside the widget rather than at the form's fields:
 * a token that did not pass, the check that could not be made just now (nothing typed is lost), or a
 * fault in the installation's settings - each in this form's own words.
 */
function BotCheckRefusal({ action }: { action: 'signup' | 'company' | 'form' | 'report' }) {
  const { t } = useI18n();
  const error = useFieldError('turnstileToken');
  if (!error) return null;
  const code = isApiError(error.cause) ? error.cause.code : null;
  const message = code === 'TURNSTILE_UNAVAILABLE' ? t(`bots.unavailable.${action}`) : code === 'TURNSTILE_MISCONFIGURED' ? t(`bots.ourFault.${action}`) : error.message;
  return (
    <p className="field-error bot-check-refusal" role="alert">
      {message}
    </p>
  );
}
